import React, { useEffect, useState } from 'react'
import { Button, Form, ListGroup, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { SETTINGS_KEYS, environment } from '../../config/environment'
import { useSetting } from '../../hooks/useSettings'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { workflowService } from '../../services/workflow.service'
import { Workflow } from '../../types'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { IfPermissions } from '../common/IfPermissions'
import PageHeader from '../common/PageHeader'
import { WorkflowEditDialog } from './WorkflowEditDialog'
import './WorkflowsList.scss'
import { WorkflowVisualizationDialog } from './WorkflowVisualizationDialog'

const WorkflowsList: React.FC = () => {
  const { t } = useTranslation()
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [loading, setLoading] = useState(true)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(null)
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null)
  const [showVisualizationDialog, setShowVisualizationDialog] = useState(false)
  const [visualizingWorkflow, setVisualizingWorkflow] = useState<Workflow | null>(null)
  const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle)

  useEffect(() => {
    document.title = `${t('workflows')} - ${appTitle}`
  }, [t, appTitle])

  useEffect(() => {
    loadWorkflows()
  }, [])

  const loadWorkflows = async () => {
    setLoading(true)
    try {
      const response = await workflowService.listAll()
      setWorkflows(response.results)
    } catch (_error) {
    } finally {
      setLoading(false)
    }
  }

  const handleEditWorkflow = (workflow?: Workflow) => {
    setEditingWorkflow(workflow || null)
    setIsCreating(!workflow)
    setShowEditDialog(true)
  }

  const handleEditSuccess = () => {
    setShowEditDialog(false)
    loadWorkflows()
  }

  const handleEditError = (_error: any) => {}

  const handleToggleEnabled = async (workflow: Workflow) => {
    if (!workflow.id) return

    try {
      await workflowService.updateWorkflow(workflow.id, {
        ...workflow,
        enabled: !workflow.enabled,
      })
      loadWorkflows()
    } catch (_error) {}
  }

  const handleDeleteWorkflow = async (workflow: Workflow) => {
    if (!workflow.id) return

    const executeDelete = async () => {
      try {
        await workflowService.deleteWorkflow(workflow.id!)
        loadWorkflows()
        setShowConfirmDialog(false)
      } catch (error) {}
    }

    setWorkflowToDelete(workflow)
    setConfirmAction(() => executeDelete)
    setShowConfirmDialog(true)
  }

  const handleCopyWorkflow = (workflow: Workflow) => {
    const copiedWorkflow: Workflow = {
      ...(JSON.parse(JSON.stringify(workflow)) as Workflow),
      name: `${workflow.name} (Copy)`,
      id: undefined,
    }
    copiedWorkflow.triggers = (copiedWorkflow.triggers || []).map((trigger) => ({
      ...trigger,
      id: undefined,
    }))
    copiedWorkflow.actions = (copiedWorkflow.actions || []).map((action) => ({
      ...action,
      id: undefined,
      email: action.email ? { ...action.email, id: undefined } : action.email,
      webhook: action.webhook ? { ...action.webhook, id: undefined } : action.webhook,
    }))
    setEditingWorkflow(copiedWorkflow)
    setIsCreating(true)
    setShowEditDialog(true)
  }

  const handleShowVisualization = (workflow: Workflow) => {
    setVisualizingWorkflow(workflow)
    setShowVisualizationDialog(true)
  }

  const getTypesList = (workflow: Workflow) => {
    if (!workflow.triggers || workflow.triggers.length === 0) {
      return '-'
    }

    const triggerTypeNames: Record<number, string> = {
      1: t('consumption'),
      2: t('document_added'),
      3: t('document_updated'),
      4: t('scheduled'),
      5: t('trigger_type_document_workflow_status_changed'),
    }

    return workflow.triggers.map((trigger) => triggerTypeNames[trigger.type] || String(trigger.type)).join(', ')
  }

  return (
    <div className="workflows-list">
      <PageHeader title={t('workflows')} info={t('workflows_info')} infoLink="workflows">
        <IfPermissions action={PermissionAction.Add} type={PermissionType.Workflow}>
          <Button variant="outline-primary" size="sm" onClick={() => handleEditWorkflow()}>
            <i className="bi bi-plus-circle me-1"></i>
            {t('add_workflow')}
          </Button>
        </IfPermissions>
      </PageHeader>

      <ListGroup>
        <ListGroup.Item className="fw-bold">
          <div className="row">
            <div className="col">{t('name')}</div>
            <div className="col d-none d-sm-flex">{t('sort_order')}</div>
            <div className="col">{t('status')}</div>
            <div className="col d-none d-sm-flex">{t('triggers')}</div>
            <div className="col text-end">{t('actions')}</div>
          </div>
        </ListGroup.Item>

        {loading && workflows.length === 0 && (
          <ListGroup.Item>
            <Spinner animation="border" size="sm" className="me-2" />
            {t('loading')}
          </ListGroup.Item>
        )}

        {workflows.map((workflow) => (
          <ListGroup.Item key={workflow.id}>
            <div className="row align-items-center">
              <div className="col">
                <Button variant="link" className="p-0 text-start" onClick={() => handleEditWorkflow(workflow)}>
                  {workflow.name}
                </Button>
              </div>
              <div className="col d-none d-sm-flex">
                <span className="text-muted">{workflow.order}</span>
              </div>
              <div className="col">
                <IfPermissions action={PermissionAction.Change} type={PermissionType.Workflow}>
                  <Form.Check
                    type="switch"
                    id={`workflow-${workflow.id}`}
                    checked={workflow.enabled}
                    onChange={() => handleToggleEnabled(workflow)}
                    label={<span className="text-muted">{workflow.enabled ? t('enabled') : t('disabled')}</span>}
                  />
                </IfPermissions>
              </div>
              <div className="col d-none d-sm-flex">{getTypesList(workflow)}</div>
              <div className="col text-end">
                <div className="d-flex gap-1 justify-content-end">
                  <button
                    className="btn btn-sm btn-outline-info rounded-start rounded-end-0"
                    onClick={() => handleShowVisualization(workflow)}
                    title={t('workflow_visualization')}
                  >
                    <i className="bi bi-diagram-3"></i>
                  </button>
                  <IfPermissions action={PermissionAction.Change} type={PermissionType.Workflow}>
                    <button className="btn btn-sm btn-outline-secondary rounded-0" onClick={() => handleEditWorkflow(workflow)} title={t('edit')}>
                      <i className="bi bi-pencil"></i>
                    </button>
                  </IfPermissions>
                  <IfPermissions action={PermissionAction.Add} type={PermissionType.Workflow}>
                    <button className="btn btn-sm btn-outline-secondary rounded-0" onClick={() => handleCopyWorkflow(workflow)} title={t('copy')}>
                      <i className="bi bi-files"></i>
                    </button>
                  </IfPermissions>
                  <IfPermissions action={PermissionAction.Delete} type={PermissionType.Workflow}>
                    <button
                      className="btn btn-sm btn-outline-danger rounded-end rounded-start-0"
                      onClick={() => handleDeleteWorkflow(workflow)}
                      title={t('delete')}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </IfPermissions>
                </div>
              </div>
            </div>
          </ListGroup.Item>
        ))}

        {!loading && workflows.length === 0 && <ListGroup.Item>{t('no_workflows_defined')}</ListGroup.Item>}
      </ListGroup>

      {showEditDialog && (
        <WorkflowEditDialog
          workflow={editingWorkflow}
          isCreating={isCreating}
          onSuccess={handleEditSuccess}
          onError={handleEditError}
          onClose={() => setShowEditDialog(false)}
        />
      )}

      <ConfirmDialog
        show={showConfirmDialog}
        title={t('confirm')}
        message={
          t('confirm_delete_workflow', { name: workflowToDelete?.name }) || `Da li zaista želite da obrišete workflow "${workflowToDelete?.name}"?`
        }
        onConfirm={() => {
          if (confirmAction) {
            confirmAction()
          }
        }}
        onCancel={() => setShowConfirmDialog(false)}
        variant="danger"
      />

      {showVisualizationDialog && visualizingWorkflow && (
        <WorkflowVisualizationDialog
          workflow={visualizingWorkflow}
          onClose={() => {
            setShowVisualizationDialog(false)
            setVisualizingWorkflow(null)
          }}
        />
      )}
    </div>
  )
}

export default WorkflowsList
